/**
 * CounterpartiesPage - Counterparty (Tiers) Management
 * 
 * Displays and manages counterparties with:
 * - List of all counterparties
 * - Create/Edit/Delete counterparties
 * - Purge unused counterparties
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useCounterpartyStore } from '../store/counterpartyStore';
import { useAuthStore } from '../store/authStore';
import type { Counterparty, CreateCounterpartyRequest, UpdateCounterpartyRequest } from '@budgira/shared';
import EmojiPicker from '../components/EmojiPicker';
import CategorySelector from '../components/CategorySelector';
import { SearchableSelect, SearchableSelectOption } from '../components/common/SearchableSelect';

/**
 * Helper function to format source labels from comma-separated values
 * @param source - Comma-separated sources like "operation,scheduler,interest"
 * @param t - Translation function
 * @returns Formatted string like "Opérations, Échéanciers"
 */
const formatSourceLabel = (source: string | undefined, t: (key: string) => string): string => {
  if (!source) return '';
  
  const sourceMap: Record<string, string> = {
    operation: t('counterparties.sources.operation'),
    scheduler: t('counterparties.sources.scheduler'),
    interest: t('counterparties.sources.interest'),
  };
  
  return source
    .split(',')
    .map(s => sourceMap[s.trim()] || s)
    .join(', ');
};

// Icons
const PlusIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
  </svg>
);

const PencilIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
  </svg>
);

const TrashIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const MergeIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
  </svg>
);

const UserIcon = () => (
  <svg className="w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
  </svg>
);

const PurgeIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const SearchIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
  </svg>
);

const TagIcon = () => (
  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
  </svg>
);

interface CounterpartyNodeProps {
  counterparty: Counterparty;
  uniqueCategory?: { id: string; name: string; logoUrl: string | null } | null;
  onEdit: (cp: Counterparty) => void;
  onDelete: (cp: Counterparty) => void;
  onBulkCategory: (cp: Counterparty) => void;
  onMerge: (cp: Counterparty) => void;
  canEdit?: boolean;
}

const CounterpartyNode = ({ counterparty, uniqueCategory, onEdit, onDelete, onBulkCategory, onMerge, canEdit = true }: CounterpartyNodeProps) => {
  const { t } = useTranslation();
  return (
    <div className="flex items-center py-2 sm:py-3 px-2 sm:px-4 hover:bg-slate-50 dark:hover:bg-slate-700/50 rounded-lg transition-colors">
      {/* Logo/Icon */}
      <span className="mr-2 sm:mr-3 text-lg sm:text-xl flex-shrink-0">
        {counterparty.logoUrl || <UserIcon />}
      </span>

      {/* Name, Account and Category */}
      <div className="flex-1 min-w-0">
        <span className="font-medium text-sm sm:text-base text-slate-700 dark:text-slate-200 truncate block">
          {counterparty.name}
        </span>
        <div className="flex items-center gap-2 sm:gap-3 mt-0.5 flex-wrap">
          {counterparty.soleAccountName && (
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-xs sm:text-sm">🏦</span>
              <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {counterparty.soleAccountName}
              </span>
            </div>
          )}
          {uniqueCategory && (
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-xs sm:text-sm">{uniqueCategory.logoUrl || '📁'}</span>
              <span className="text-xs text-slate-500 dark:text-slate-400 truncate">
                {uniqueCategory.name}
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Actions - only show when canEdit is true */}
      {canEdit && (
        <div className="flex items-center gap-0.5 sm:gap-1 flex-shrink-0">
          <button
            onClick={() => onBulkCategory(counterparty)}
            className="p-1 sm:p-1.5 rounded-lg hover:bg-blue-100 dark:hover:bg-blue-900/50 text-slate-400 dark:text-slate-500 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            title={t('counterparties.bulkCategory.button', 'Changer la catégorie')}
          >
            <TagIcon />
          </button>
          <button
            onClick={() => onMerge(counterparty)}
            className="p-1 sm:p-1.5 rounded-lg hover:bg-teal-100 dark:hover:bg-teal-900/50 text-slate-400 dark:text-slate-500 hover:text-teal-600 dark:hover:text-teal-400 transition-colors"
            title={t('counterparties.merge.button', 'Fusionner avec un autre tiers')}
          >
            <MergeIcon />
          </button>
          <button
            onClick={() => onEdit(counterparty)}
            className="p-1 sm:p-1.5 rounded-lg hover:bg-violet-100 dark:hover:bg-violet-900/50 text-slate-400 dark:text-slate-500 hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
            title={t('common.edit', 'Modifier')}
          >
            <PencilIcon />
          </button>
          {/* Only show delete button if counterparty is not used */}
          {!counterparty.hasOperations && !counterparty.hasSchedulers && !counterparty.hasInterestLinks && (
            <button
              onClick={() => onDelete(counterparty)}
              className="p-1 sm:p-1.5 rounded-lg hover:bg-rose-100 dark:hover:bg-rose-900/50 text-slate-400 dark:text-slate-500 hover:text-rose-600 dark:hover:text-rose-400 transition-colors"
              title={t('common.delete', 'Supprimer')}
            >
              <TrashIcon />
            </button>
          )}
        </div>
      )}
    </div>
  );
};

interface CounterpartyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateCounterpartyRequest | UpdateCounterpartyRequest) => Promise<void>;
  onRenameForAccount?: (name: string, logoUrl: string | undefined, accountId: string, operationType?: 'debit' | 'credit') => Promise<{ operationsUpdated: number; schedulersUpdated: number } | null>;
  editCounterparty?: Counterparty | null;
  availableAccounts?: { id: string; name: string; source?: string }[];
  isLoadingAccounts?: boolean;
  initialOperationType?: 'debit' | 'credit';
}

const CounterpartyModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  onRenameForAccount,
  editCounterparty, 
  availableAccounts = [],
  isLoadingAccounts = false,
  initialOperationType,
}: CounterpartyModalProps) => {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [selectedAccountId, setSelectedAccountId] = useState<string | undefined>(undefined);
  const [selectedOperationType, setSelectedOperationType] = useState<'debit' | 'credit' | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const emojiButtonRef = useRef<HTMLButtonElement>(null);

  // Determine if this is a partial rename (editing and account selected)
  const isPartialRename = editCounterparty && selectedAccountId;

  useEffect(() => {
    if (editCounterparty) {
      setName(editCounterparty.name);
      setLogoUrl(editCounterparty.logoUrl || '');
    } else {
      setName('');
      setLogoUrl('');
    }
    setSelectedAccountId(undefined);
    setSelectedOperationType(undefined);
  }, [editCounterparty, isOpen]);

  // Update operation type when initialOperationType changes (after async load)
  useEffect(() => {
    if (initialOperationType) {
      setSelectedOperationType(initialOperationType);
    }
  }, [initialOperationType]);

  // Auto-select account if only one available
  useEffect(() => {
    if (editCounterparty && availableAccounts.length === 1) {
      setSelectedAccountId(availableAccounts[0]!.id);
    }
  }, [editCounterparty, availableAccounts]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    // Operation type is required when renaming for account
    if (isPartialRename && !selectedOperationType) return;

    setIsSubmitting(true);
    try {
      if (isPartialRename && onRenameForAccount) {
        // Rename for specific account only
        await onRenameForAccount(name.trim(), logoUrl.trim() || undefined, selectedAccountId!, selectedOperationType);
      } else {
        // Normal create/update
        await onSave({
          name: name.trim(),
          logoUrl: logoUrl.trim() || undefined,
        });
      }
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
        <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
          {editCounterparty 
            ? t('counterparties.modal.editTitle', 'Modifier le tiers') 
            : t('counterparties.modal.createTitle', 'Nouveau tiers')}
        </h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('counterparties.modal.name', 'Nom')} *
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
              placeholder={t('counterparties.modal.namePlaceholder', 'Ex: Carrefour, EDF...')}
              required
              autoFocus
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
              {t('counterparties.modal.icon', 'Icône')}
            </label>
            <div className="relative">
              <button
                ref={emojiButtonRef}
                type="button"
                onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg focus:ring-2 focus:ring-violet-500 focus:border-violet-500 bg-white dark:bg-slate-700 text-left flex items-center gap-2"
              >
                {logoUrl ? (
                  <span className="text-xl">{logoUrl}</span>
                ) : (
                  <span className="text-slate-400 dark:text-slate-500">
                    {t('counterparties.modal.selectIcon', 'Sélectionner une icône...')}
                  </span>
                )}
              </button>
              {showEmojiPicker && (
                <EmojiPicker
                  value={logoUrl}
                  onChange={(emoji) => {
                    setLogoUrl(emoji);
                    setShowEmojiPicker(false);
                  }}
                  onClose={() => setShowEmojiPicker(false)}
                />
              )}
            </div>
            {logoUrl && (
              <button
                type="button"
                onClick={() => setLogoUrl('')}
                className="mt-1 text-sm text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300"
              >
                {t('counterparties.modal.removeIcon', 'Supprimer l\'icône')}
              </button>
            )}
          </div>

          {/* Account filter - only show when editing and counterparty is used in multiple accounts */}
          {editCounterparty && availableAccounts.length > 0 && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('counterparties.modal.accountScope', 'Appliquer sur')}
              </label>
              {isLoadingAccounts ? (
                <div className="px-3 py-2 text-slate-500 dark:text-slate-400">
                  {t('common.loading', 'Chargement...')}
                </div>
              ) : availableAccounts.length === 1 ? (
                // Single account - show as read-only
                <div className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300">
                  <div className="flex items-center justify-between">
                    <span>🏦 {availableAccounts[0]!.name}</span>
                    {availableAccounts[0]!.source && (
                      <span className="text-xs text-slate-500 dark:text-slate-400 italic">
                        {t('counterparties.sources.linkedVia', 'Lié via')} : {formatSourceLabel(availableAccounts[0]!.source, t)}
                      </span>
                    )}
                  </div>
                </div>
              ) : (
                // Multiple accounts - show select
                <>
                  <select
                    value={selectedAccountId || ''}
                    onChange={(e) => setSelectedAccountId(e.target.value || undefined)}
                    className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                  >
                    <option value="">{t('counterparties.allAccounts', 'Tous les comptes')}</option>
                    {availableAccounts.map(account => (
                      <option key={account.id} value={account.id}>
                        {account.name} ({formatSourceLabel(account.source, t)})
                      </option>
                    ))}
                  </select>
                  {selectedAccountId && (
                    <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
                      ℹ️ {t('counterparties.modal.partialRenameNote', 'Un nouveau tiers sera créé pour ce compte. L\'ancien restera pour les autres comptes.')}
                    </p>
                  )}
                </>
              )}
            </div>
          )}

          {/* Operation type filter - only show when partial rename (account selected) */}
          {isPartialRename && (
            <div>
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('counterparties.operationType.label', 'Type d\'opération')} *
              </label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="operationType"
                    value="debit"
                    checked={selectedOperationType === 'debit'}
                    onChange={() => setSelectedOperationType('debit')}
                    className="w-4 h-4 text-violet-600 border-slate-300 focus:ring-violet-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {t('counterparties.operationType.debit', 'Débit (dépenses)')}
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="operationType"
                    value="credit"
                    checked={selectedOperationType === 'credit'}
                    onChange={() => setSelectedOperationType('credit')}
                    className="w-4 h-4 text-violet-600 border-slate-300 focus:ring-violet-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {t('counterparties.operationType.credit', 'Crédit (revenus)')}
                  </span>
                </label>
              </div>
            </div>
          )}

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
            >
              {t('common.cancel', 'Annuler')}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || !name.trim() || !!(isPartialRename && !selectedOperationType)}
              className="flex-1 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isSubmitting ? t('common.saving', 'Enregistrement...') : t('common.save', 'Enregistrer')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default function CounterpartiesPage({ embedded = false, canEdit = true }: { embedded?: boolean; canEdit?: boolean }) {
  const { t } = useTranslation();
  const {
    counterparties,
    isLoading,
    error,
    fetchCounterparties,
    createCounterparty,
    updateCounterparty,
    deleteCounterparty,
    purgeUnused,
    getOperationsCount,
    getUniqueCategory,
    getAccountsForCounterparty,
    getOperationTypes,
    updateBulkCategory,
    mergeCounterparties,
    renameForAccount,
    clearError,
  } = useCounterpartyStore();

  const { user } = useAuthStore();

  // Helper function to filter accounts based on shared access permissions
  // For shared users, only show accounts they have access to
  // @param accounts - The accounts to filter
  // @param requireWrite - If true, only return accounts with write permission (for modifications)
  const filterAccessibleAccounts = useCallback((
    accounts: { id: string; name: string; source?: string }[],
    requireWrite: boolean = false
  ) => {
    if (!user?.sharedAccess) {
      // Not a shared user - return all accounts
      return accounts;
    }
    
    const accessibleAccountIds = new Set(
      user.sharedAccess.accountPermissions
        .filter(ap => requireWrite ? ap.permission === 'write' : (ap.permission === 'read' || ap.permission === 'write'))
        .map(ap => ap.accountId)
    );
    
    return accounts.filter(account => accessibleAccountIds.has(account.id));
  }, [user]);

  // Helper function to check if a counterparty can be edited by the current user
  // For shared users, check if the counterparty has at least one writable account
  const canEditCounterparty = useCallback((counterparty: Counterparty): boolean => {
    if (!user?.sharedAccess) {
      // Not a shared user - can edit all
      return true;
    }
    
    const writableAccountIds = new Set(
      user.sharedAccess.accountPermissions
        .filter(ap => ap.permission === 'write')
        .map(ap => ap.accountId)
    );
    
    // If the counterparty has a sole account, check if it's writable
    if (counterparty.soleAccountId) {
      return writableAccountIds.has(counterparty.soleAccountId);
    }
    
    // For multi-account counterparties, we can't know without loading accounts
    // So we check if the user has ANY writable account - if not, they can't edit anything
    // The actual validation will happen when the modal opens and filters accounts
    return writableAccountIds.size > 0;
  }, [user]);

  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editCounterparty, setEditCounterparty] = useState<Counterparty | null>(null);
  const [editAvailableAccounts, setEditAvailableAccounts] = useState<{ id: string; name: string; source?: string }[]>([]);
  const [isEditLoadingAccounts, setIsEditLoadingAccounts] = useState(false);
  const [editInitialOperationType, setEditInitialOperationType] = useState<'debit' | 'credit' | undefined>(undefined);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [counterpartyToDelete, setCounterpartyToDelete] = useState<Counterparty | null>(null);
  const [showPurgeModal, setShowPurgeModal] = useState(false);
  const [isPurging, setIsPurging] = useState(false);
  const [_purgeResult, setPurgeResult] = useState<number | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  
  // Unique categories cache
  const [uniqueCategories, setUniqueCategories] = useState<Record<string, { id: string; name: string; logoUrl: string | null } | null>>({});
  
  // Bulk category modal state
  const [showBulkCategoryModal, setShowBulkCategoryModal] = useState(false);
  const [bulkCategoryCounterparty, setBulkCategoryCounterparty] = useState<Counterparty | null>(null);
  const [bulkCategoryId, setBulkCategoryId] = useState<string | undefined>(undefined);
  const [bulkCategoryAccountId, setBulkCategoryAccountId] = useState<string | undefined>(undefined);
  const [bulkCategoryOperationType, setBulkCategoryOperationType] = useState<'debit' | 'credit' | undefined>(undefined);
  const [bulkCategoryAvailableAccounts, setBulkCategoryAvailableAccounts] = useState<{ id: string; name: string; source?: string }[]>([]);
  const [bulkCategoryOperationsCount, setBulkCategoryOperationsCount] = useState<number>(0);
  const [isBulkCategoryLoading, setIsBulkCategoryLoading] = useState(false);

  // Merge modal state
  const [showMergeModal, setShowMergeModal] = useState(false);
  const [mergeTargetCounterparty, setMergeTargetCounterparty] = useState<Counterparty | null>(null);
  const [mergeSourceCounterpartyId, setMergeSourceCounterpartyId] = useState<string | undefined>(undefined);
  const [mergeAccountId, setMergeAccountId] = useState<string | undefined>(undefined);
  const [mergeOperationType, setMergeOperationType] = useState<'debit' | 'credit' | undefined>(undefined);
  const [mergeAvailableAccounts, setMergeAvailableAccounts] = useState<{ id: string; name: string; source?: string }[]>([]);
  const [isMerging, setIsMerging] = useState(false);

  useEffect(() => {
    fetchCounterparties();
  }, [fetchCounterparties]);

  // Load unique categories for visible counterparties
  useEffect(() => {
    const loadUniqueCategories = async () => {
      const filtered = counterparties.filter((cp) =>
        cp.name.toLowerCase().includes(searchTerm.toLowerCase())
      );
      
      for (const cp of filtered) {
        if (uniqueCategories[cp.id] === undefined) {
          const category = await getUniqueCategory(cp.id);
          setUniqueCategories(prev => ({ ...prev, [cp.id]: category }));
        }
      }
    };
    
    if (counterparties.length > 0) {
      loadUniqueCategories();
    }
  }, [counterparties, searchTerm, getUniqueCategory]);

  // Filter counterparties based on search
  const filteredCounterparties = counterparties.filter((cp) =>
    cp.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleCreate = () => {
    setEditCounterparty(null);
    setEditAvailableAccounts([]);
    setEditInitialOperationType(undefined);
    setShowModal(true);
  };

  const handleEdit = async (cp: Counterparty) => {
    setEditCounterparty(cp);
    setEditAvailableAccounts([]);
    setEditInitialOperationType(undefined);
    setIsEditLoadingAccounts(true);
    setShowModal(true);
    
    // Load accounts for this counterparty
    try {
      const accounts = await getAccountsForCounterparty(cp.id);
      // Filter to only writable accounts for shared users (modifications require write access)
      const accessibleAccounts = filterAccessibleAccounts(accounts, true);
      setEditAvailableAccounts(accessibleAccounts);
      
      // Load operation types and auto-select if only one type exists
      // Use the first account ID if only one account, otherwise all accounts
      const accountIdForTypes = accessibleAccounts.length === 1 ? accessibleAccounts[0]!.id : undefined;
      const operationTypes = await getOperationTypes(cp.id, accountIdForTypes);
      if (operationTypes.hasDebits && !operationTypes.hasCredits) {
        setEditInitialOperationType('debit');
      } else if (!operationTypes.hasDebits && operationTypes.hasCredits) {
        setEditInitialOperationType('credit');
      }
    } finally {
      setIsEditLoadingAccounts(false);
    }
  };

  const handleDelete = (cp: Counterparty) => {
    setCounterpartyToDelete(cp);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!counterpartyToDelete) return;
    
    const success = await deleteCounterparty(counterpartyToDelete.id);
    if (success) {
      setSuccessMessage(t('counterparties.deleteSuccess', 'Tiers supprimé avec succès'));
      setTimeout(() => setSuccessMessage(null), 3000);
    }
    setShowDeleteModal(false);
    setCounterpartyToDelete(null);
  };

  const handleSave = async (data: CreateCounterpartyRequest | UpdateCounterpartyRequest) => {
    if (editCounterparty) {
      await updateCounterparty(editCounterparty.id, data);
    } else {
      await createCounterparty(data as CreateCounterpartyRequest);
    }
  };

  const handleRenameForAccount = async (
    name: string,
    logoUrl: string | undefined,
    accountId: string,
    operationType?: 'debit' | 'credit'
  ): Promise<{ operationsUpdated: number; schedulersUpdated: number } | null> => {
    if (!editCounterparty) return null;
    
    const result = await renameForAccount(editCounterparty.id, name, logoUrl, accountId, operationType);
    if (result) {
      setSuccessMessage(
        t('counterparties.renameForAccount.success', 
          'Renommage réussi : {{operations}} opération(s) et {{schedulers}} échéancier(s) transféré(s) vers "{{name}}"', 
          { operations: result.operationsUpdated, schedulers: result.schedulersUpdated, name }
        )
      );
      setTimeout(() => setSuccessMessage(null), 5000);
      return { operationsUpdated: result.operationsUpdated, schedulersUpdated: result.schedulersUpdated };
    }
    return null;
  };

  const handlePurgeUnused = async () => {
    setIsPurging(true);
    try {
      const count = await purgeUnused();
      setPurgeResult(count);
      if (count > 0) {
        setSuccessMessage(t('counterparties.purgeSuccess', 'Tiers non utilisés supprimés : {{count}}', { count }));
      } else {
        setSuccessMessage(t('counterparties.purgeNone', 'Aucun tiers non utilisé à supprimer'));
      }
      setTimeout(() => {
        setSuccessMessage(null);
        setPurgeResult(null);
      }, 3000);
    } finally {
      setIsPurging(false);
      setShowPurgeModal(false);
    }
  };

  const handleBulkCategory = async (cp: Counterparty) => {
    setBulkCategoryCounterparty(cp);
    setBulkCategoryId(undefined);
    setBulkCategoryOperationType(undefined);
    setIsBulkCategoryLoading(true);
    
    try {
      // Load accounts for this counterparty
      const cpAccounts = await getAccountsForCounterparty(cp.id);
      // Filter to only writable accounts for shared users (modifications require write access)
      const accessibleAccounts = filterAccessibleAccounts(cpAccounts, true);
      setBulkCategoryAvailableAccounts(accessibleAccounts);
      
      // Determine the account ID to use for fetching operation types
      let accountIdForTypes: string | undefined;
      
      // If only one account, preselect it
      if (accessibleAccounts.length === 1) {
        setBulkCategoryAccountId(accessibleAccounts[0]!.id);
        accountIdForTypes = accessibleAccounts[0]!.id;
        const count = await getOperationsCount(cp.id, accessibleAccounts[0]!.id);
        setBulkCategoryOperationsCount(count);
      } else {
        setBulkCategoryAccountId(undefined);
        const count = await getOperationsCount(cp.id);
        setBulkCategoryOperationsCount(count);
      }
      
      // Load operation types and auto-select if only one type exists
      const operationTypes = await getOperationTypes(cp.id, accountIdForTypes);
      if (operationTypes.hasDebits && !operationTypes.hasCredits) {
        setBulkCategoryOperationType('debit');
      } else if (!operationTypes.hasDebits && operationTypes.hasCredits) {
        setBulkCategoryOperationType('credit');
      }
      // If both or neither, leave undefined (user must choose)
    } finally {
      setIsBulkCategoryLoading(false);
    }
    setShowBulkCategoryModal(true);
  };

  // Update operations count and operation types when account filter changes
  const handleBulkCategoryAccountChange = async (accountId: string | undefined) => {
    setBulkCategoryAccountId(accountId);
    setBulkCategoryOperationType(undefined); // Reset operation type when account changes
    if (bulkCategoryCounterparty) {
      setIsBulkCategoryLoading(true);
      try {
        const count = await getOperationsCount(bulkCategoryCounterparty.id, accountId);
        setBulkCategoryOperationsCount(count);
        
        // Reload operation types for new account filter and auto-select if only one type
        const operationTypes = await getOperationTypes(bulkCategoryCounterparty.id, accountId);
        if (operationTypes.hasDebits && !operationTypes.hasCredits) {
          setBulkCategoryOperationType('debit');
        } else if (!operationTypes.hasDebits && operationTypes.hasCredits) {
          setBulkCategoryOperationType('credit');
        }
      } finally {
        setIsBulkCategoryLoading(false);
      }
    }
  };

  const confirmBulkCategory = async () => {
    if (!bulkCategoryCounterparty || !bulkCategoryId || !bulkCategoryOperationType) return;
    
    setIsBulkCategoryLoading(true);
    try {
      const count = await updateBulkCategory(bulkCategoryCounterparty.id, bulkCategoryId, bulkCategoryAccountId, bulkCategoryOperationType);
      
      // Refresh the unique category for this counterparty
      const newCategory = await getUniqueCategory(bulkCategoryCounterparty.id);
      setUniqueCategories(prev => ({ ...prev, [bulkCategoryCounterparty.id]: newCategory }));
      
      setSuccessMessage(t('counterparties.bulkCategory.success', '{{count}} opération(s) mise(s) à jour', { count }));
      setTimeout(() => setSuccessMessage(null), 3000);
      setShowBulkCategoryModal(false);
      setBulkCategoryCounterparty(null);
      setBulkCategoryId(undefined);
      setBulkCategoryAccountId(undefined);
      setBulkCategoryOperationType(undefined);
    } finally {
      setIsBulkCategoryLoading(false);
    }
  };

  const handleMerge = async (cp: Counterparty) => {
    setMergeTargetCounterparty(cp);
    setMergeSourceCounterpartyId(undefined);
    setMergeOperationType(undefined);
    
    // Load accounts for this counterparty
    const cpAccounts = await getAccountsForCounterparty(cp.id);
    // Filter to only writable accounts for shared users (modifications require write access)
    const accessibleAccounts = filterAccessibleAccounts(cpAccounts, true);
    setMergeAvailableAccounts(accessibleAccounts);
    
    // Determine the account ID to use for fetching operation types
    let accountIdForTypes: string | undefined;
    
    // If only one account, preselect it
    if (accessibleAccounts.length === 1) {
      setMergeAccountId(accessibleAccounts[0]!.id);
      accountIdForTypes = accessibleAccounts[0]!.id;
    } else {
      setMergeAccountId(undefined);
    }
    
    // Load operation types and auto-select if only one type exists
    const operationTypes = await getOperationTypes(cp.id, accountIdForTypes);
    if (operationTypes.hasDebits && !operationTypes.hasCredits) {
      setMergeOperationType('debit');
    } else if (!operationTypes.hasDebits && operationTypes.hasCredits) {
      setMergeOperationType('credit');
    }
    // If both or neither, leave undefined (user must choose)
    
    setShowMergeModal(true);
  };

  const confirmMerge = async () => {
    if (!mergeTargetCounterparty || !mergeSourceCounterpartyId || !mergeOperationType) return;
    
    setIsMerging(true);
    try {
      const result = await mergeCounterparties(mergeTargetCounterparty.id, mergeSourceCounterpartyId, mergeAccountId, mergeOperationType);
      
      if (result) {
        // Clear the unique categories cache for the merged counterparty (only if no filters applied)
        if (!mergeAccountId && !mergeOperationType) {
          setUniqueCategories(prev => {
            const newState = { ...prev };
            delete newState[mergeSourceCounterpartyId];
            return newState;
          });
        }
        
        setSuccessMessage(
          t('counterparties.merge.success', 'Fusion réussie : {{operations}} opération(s) et {{schedulers}} échéancier(s) transféré(s)', { 
            operations: result.operationsUpdated, 
            schedulers: result.schedulersUpdated 
          })
        );
        setTimeout(() => setSuccessMessage(null), 5000);
      }
      
      setShowMergeModal(false);
      setMergeTargetCounterparty(null);
      setMergeSourceCounterpartyId(undefined);
      setMergeAccountId(undefined);
      setMergeOperationType(undefined);
    } finally {
      setIsMerging(false);
    }
  };

  // Get options for merge source select (exclude the target counterparty)
  const mergeSourceOptions: SearchableSelectOption[] = counterparties
    .filter(cp => cp.id !== mergeTargetCounterparty?.id)
    .map(cp => ({
      id: cp.id,
      label: cp.logoUrl ? `${cp.logoUrl} ${cp.name}` : cp.name,
    }));

  return (
    <div className={embedded ? '' : 'container mx-auto px-4 py-6 max-w-4xl overflow-x-hidden'}>
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-100">
            {t('counterparties.title', 'Gestion des tiers')}
          </h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">
            {t('counterparties.subtitle', 'Gérez vos tiers (commerçants, fournisseurs...)')}
          </p>
        </div>

        {/* Action buttons - only show when canEdit is true */}
        {canEdit && (
          <div className="flex flex-wrap items-center gap-2">
            {/* Purge unused button */}
            <button
              onClick={() => setShowPurgeModal(true)}
              disabled={isLoading || isPurging}
              className="flex items-center gap-2 px-3 py-2 text-sm text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/30 rounded-lg hover:bg-amber-100 dark:hover:bg-amber-900/50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              title={t('counterparties.purgeUnused', 'Épurer les non utilisés')}
            >
              <PurgeIcon />
              <span className="hidden md:inline">{t('counterparties.purgeUnused', 'Épurer')}</span>
            </button>

            {/* Create button */}
            <button
              onClick={handleCreate}
              disabled={isLoading}
              className="flex items-center gap-2 px-4 py-2 bg-violet-600 text-white rounded-lg hover:bg-violet-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <PlusIcon />
              <span>{t('counterparties.create', 'Nouveau tiers')}</span>
            </button>
          </div>
        )}
      </div>

      {/* Success message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300">
          {successMessage}
        </div>
      )}

      {/* Error message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 flex items-center justify-between">
          <span>{error}</span>
          <button onClick={clearError} className="text-red-500 hover:text-red-700">
            ✕
          </button>
        </div>
      )}

      {/* Search */}
      <div className="mb-4">
        <div className="relative">
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400">
            <SearchIcon />
          </span>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder={t('counterparties.searchPlaceholder', 'Rechercher un tiers...')}
            className="w-full pl-10 pr-4 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
          />
        </div>
      </div>

      {/* List */}
      <div className="bg-white dark:bg-slate-800 rounded-xl shadow-sm border border-slate-200 dark:border-slate-700">
        {isLoading ? (
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">
            {t('common.loading', 'Chargement...')}
          </div>
        ) : filteredCounterparties.length === 0 ? (
          <div className="p-8 text-center text-slate-500 dark:text-slate-400">
            {searchTerm
              ? t('counterparties.noResults', 'Aucun tiers trouvé pour "{{search}}"', { search: searchTerm })
              : t('counterparties.empty', 'Aucun tiers. Créez-en un pour commencer.')}
          </div>
        ) : (
          <div className="divide-y divide-slate-100 dark:divide-slate-700">
            {filteredCounterparties.map((cp) => (
              <CounterpartyNode
                key={cp.id}
                counterparty={cp}
                uniqueCategory={uniqueCategories[cp.id]}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onBulkCategory={handleBulkCategory}
                onMerge={handleMerge}
                canEdit={canEdit && canEditCounterparty(cp)}
              />
            ))}
          </div>
        )}
      </div>

      {/* Stats */}
      <div className="mt-4 text-sm text-slate-500 dark:text-slate-400 text-center">
        {t('counterparties.count', '{{count}} tiers', { count: counterparties.length })}
      </div>

      {/* Create/Edit Modal */}
      <CounterpartyModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSave}
        onRenameForAccount={handleRenameForAccount}
        editCounterparty={editCounterparty}
        availableAccounts={editAvailableAccounts}
        isLoadingAccounts={isEditLoadingAccounts}
        initialOperationType={editInitialOperationType}
      />

      {/* Delete Confirmation Modal */}
      {showDeleteModal && counterpartyToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowDeleteModal(false)} />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
              {t('counterparties.deleteModal.title', 'Supprimer le tiers')}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              {t('counterparties.deleteModal.message', 'Êtes-vous sûr de vouloir supprimer le tiers "{{name}}" ? Cette action est irréversible.', { name: counterpartyToDelete.name })}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                {t('common.cancel', 'Annuler')}
              </button>
              <button
                onClick={confirmDelete}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors"
              >
                {t('common.delete', 'Supprimer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Purge Unused Modal */}
      {showPurgeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowPurgeModal(false)} />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
              {t('counterparties.purgeModal.title', 'Épurer les tiers non utilisés')}
            </h2>
            <p className="text-slate-600 dark:text-slate-300 mb-6">
              {t('counterparties.purgeModal.message', 'Cette action supprimera tous les tiers qui ne sont associés à aucune opération ni aucun échéancier. Cette action est irréversible.')}
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowPurgeModal(false)}
                className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                {t('common.cancel', 'Annuler')}
              </button>
              <button
                onClick={handlePurgeUnused}
                disabled={isPurging}
                className="flex-1 px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:opacity-50 transition-colors"
              >
                {isPurging ? t('common.loading', 'Chargement...') : t('counterparties.purgeModal.confirm', 'Épurer')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Category Modal */}
      {showBulkCategoryModal && bulkCategoryCounterparty && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowBulkCategoryModal(false)} />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
              {t('counterparties.bulkCategory.title', 'Changer la catégorie')}
            </h2>
            
            <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                <span className="font-medium">{t('counterparties.bulkCategory.counterparty', 'Tiers')} :</span>{' '}
                {bulkCategoryCounterparty.logoUrl && <span className="mr-1">{bulkCategoryCounterparty.logoUrl}</span>}
                {bulkCategoryCounterparty.name}
              </p>
              <p className="text-sm text-slate-600 dark:text-slate-300 mt-1">
                <span className="font-medium">{t('counterparties.bulkCategory.operationsCount', 'Opérations concernées')} :</span>{' '}
                {isBulkCategoryLoading ? '...' : bulkCategoryOperationsCount}
              </p>
            </div>

            {/* Account filter - only show if counterparty is used in accounts */}
            {bulkCategoryAvailableAccounts.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('counterparties.accountFilter', 'Compte cible')}
                </label>
                {bulkCategoryAvailableAccounts.length === 1 ? (
                  // Single account - show as read-only with source info
                  <div className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300">
                    <div className="flex items-center justify-between">
                      <span>🏦 {bulkCategoryAvailableAccounts[0]!.name}</span>
                      {bulkCategoryAvailableAccounts[0]!.source && (
                        <span className="text-xs text-slate-500 dark:text-slate-400 italic">
                          {t('counterparties.sources.linkedVia', 'Lié via')} : {formatSourceLabel(bulkCategoryAvailableAccounts[0]!.source, t)}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  // Multiple accounts - show select with source info
                  <>
                    <select
                      value={bulkCategoryAccountId || ''}
                      onChange={(e) => handleBulkCategoryAccountChange(e.target.value || undefined)}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    >
                      <option value="">{t('counterparties.allAccounts', 'Tous les comptes')}</option>
                      {bulkCategoryAvailableAccounts.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.name} ({formatSourceLabel(account.source, t)})
                        </option>
                      ))}
                    </select>
                    {bulkCategoryAccountId && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1 italic">
                        {t('counterparties.sources.linkedVia', 'Lié via')} : {formatSourceLabel(bulkCategoryAvailableAccounts.find(a => a.id === bulkCategoryAccountId)?.source, t)}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}

            {bulkCategoryOperationsCount === 0 && !isBulkCategoryLoading ? (
              <p className="text-amber-600 dark:text-amber-400 mb-4">
                {t('counterparties.bulkCategory.noOperations', 'Ce tiers n\'a aucune opération associée.')}
              </p>
            ) : (
              <>
                <p className="text-slate-600 dark:text-slate-300 mb-4">
                  {t('counterparties.bulkCategory.message', 'Sélectionnez la nouvelle catégorie à appliquer à toutes les opérations de ce tiers.')}
                </p>

                {/* Operation type selector - required */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('counterparties.operationType.label', 'Type d\'opération')} *
                  </label>
                  <div className="flex gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="bulkCategoryOperationType"
                        value="debit"
                        checked={bulkCategoryOperationType === 'debit'}
                        onChange={() => { setBulkCategoryOperationType('debit'); setBulkCategoryId(undefined); }}
                        className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">
                        {t('counterparties.operationType.debit', 'Débit (dépenses)')}
                      </span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="bulkCategoryOperationType"
                        value="credit"
                        checked={bulkCategoryOperationType === 'credit'}
                        onChange={() => { setBulkCategoryOperationType('credit'); setBulkCategoryId(undefined); }}
                        className="w-4 h-4 text-blue-600 border-slate-300 focus:ring-blue-500"
                      />
                      <span className="text-sm text-slate-700 dark:text-slate-300">
                        {t('counterparties.operationType.credit', 'Crédit (revenus)')}
                      </span>
                    </label>
                  </div>
                </div>
                
                <div className="mb-6">
                  <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                    {t('counterparties.bulkCategory.category', 'Nouvelle catégorie')} *
                  </label>
                  <CategorySelector
                    value={bulkCategoryId}
                    onChange={(categoryId) => setBulkCategoryId(categoryId)}
                    placeholder={t('counterparties.bulkCategory.selectCategory', 'Sélectionner une catégorie...')}
                    filterType={bulkCategoryOperationType}
                  />
                </div>
              </>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowBulkCategoryModal(false);
                  setBulkCategoryCounterparty(null);
                  setBulkCategoryId(undefined);
                  setBulkCategoryAccountId(undefined);
                  setBulkCategoryOperationType(undefined);
                }}
                className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                {t('common.cancel', 'Annuler')}
              </button>
              {bulkCategoryOperationsCount > 0 && (
                <button
                  onClick={confirmBulkCategory}
                  disabled={isBulkCategoryLoading || !bulkCategoryId || !bulkCategoryOperationType}
                  className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {isBulkCategoryLoading ? t('common.loading', 'Chargement...') : t('counterparties.bulkCategory.confirm', 'Appliquer')}
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Merge Counterparty Modal */}
      {showMergeModal && mergeTargetCounterparty && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50" onClick={() => setShowMergeModal(false)} />
          <div className="relative bg-white dark:bg-slate-800 rounded-xl shadow-xl w-full max-w-md mx-4 p-6">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-slate-100 mb-4">
              {t('counterparties.merge.title', 'Fusionner des tiers')}
            </h2>
            
            <div className="mb-4 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg">
              <p className="text-sm text-slate-600 dark:text-slate-300">
                <span className="font-medium">{t('counterparties.merge.target', 'Tiers à conserver')} :</span>{' '}
                {mergeTargetCounterparty.logoUrl && <span className="mr-1">{mergeTargetCounterparty.logoUrl}</span>}
                {mergeTargetCounterparty.name}
              </p>
            </div>

            <p className="text-slate-600 dark:text-slate-300 mb-4">
              {t('counterparties.merge.message', 'Sélectionnez le tiers à fusionner. Toutes ses opérations et échéanciers seront transférés vers le tiers ci-dessus, puis il sera supprimé.')}
            </p>

            {/* Account filter - only show if counterparty is used in accounts */}
            {mergeAvailableAccounts.length > 0 && (
              <div className="mb-4">
                <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                  {t('counterparties.accountFilter', 'Compte cible')}
                </label>
                {mergeAvailableAccounts.length === 1 ? (
                  // Single account - show as read-only with source info
                  <div className="px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-slate-50 dark:bg-slate-700/50 text-slate-700 dark:text-slate-300">
                    <div className="flex items-center justify-between">
                      <span>🏦 {mergeAvailableAccounts[0]!.name}</span>
                      {mergeAvailableAccounts[0]!.source && (
                        <span className="text-xs text-slate-500 dark:text-slate-400 italic">
                          {t('counterparties.sources.linkedVia', 'Lié via')} : {formatSourceLabel(mergeAvailableAccounts[0]!.source, t)}
                        </span>
                      )}
                    </div>
                  </div>
                ) : (
                  // Multiple accounts - show select with source info
                  <>
                    <select
                      value={mergeAccountId || ''}
                      onChange={(e) => setMergeAccountId(e.target.value || undefined)}
                      className="w-full px-3 py-2 border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-violet-500 focus:border-violet-500"
                    >
                      <option value="">{t('counterparties.allAccounts', 'Tous les comptes')}</option>
                      {mergeAvailableAccounts.map(account => (
                        <option key={account.id} value={account.id}>
                          {account.name} ({formatSourceLabel(account.source, t)})
                        </option>
                      ))}
                    </select>
                    {mergeAccountId && (
                      <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                        <span className="italic">{t('counterparties.sources.linkedVia', 'Lié via')} : {formatSourceLabel(mergeAvailableAccounts.find(a => a.id === mergeAccountId)?.source, t)}</span>
                        <br />
                        {t('counterparties.merge.partialMergeNote', 'Note : le tiers source ne sera pas supprimé (fusion partielle)')}
                      </p>
                    )}
                  </>
                )}
              </div>
            )}
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('counterparties.merge.source', 'Tiers à fusionner (sera supprimé)')} *
              </label>
              <SearchableSelect
                options={mergeSourceOptions}
                value={mergeSourceCounterpartyId}
                onChange={(value) => setMergeSourceCounterpartyId(value)}
                placeholder={t('counterparties.merge.selectSource', 'Sélectionner un tiers...')}
                showAllOption={false}
              />
            </div>

            {/* Operation type selector - required */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-slate-700 dark:text-slate-300 mb-1">
                {t('counterparties.operationType.label', 'Type d\'opération')} *
              </label>
              <div className="flex gap-3">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="mergeOperationType"
                    value="debit"
                    checked={mergeOperationType === 'debit'}
                    onChange={() => setMergeOperationType('debit')}
                    className="w-4 h-4 text-teal-600 border-slate-300 focus:ring-teal-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {t('counterparties.operationType.debit', 'Débit (dépenses)')}
                  </span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    name="mergeOperationType"
                    value="credit"
                    checked={mergeOperationType === 'credit'}
                    onChange={() => setMergeOperationType('credit')}
                    className="w-4 h-4 text-teal-600 border-slate-300 focus:ring-teal-500"
                  />
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {t('counterparties.operationType.credit', 'Crédit (revenus)')}
                  </span>
                </label>
              </div>
            </div>

            {mergeSourceCounterpartyId && !mergeAccountId && !mergeOperationType && mergeAvailableAccounts.length !== 1 && (
              <div className="mb-4 p-3 bg-amber-50 dark:bg-amber-900/30 border border-amber-200 dark:border-amber-800 rounded-lg">
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  ⚠️ {t('counterparties.merge.warning', 'Attention : cette action est irréversible. Le tiers sélectionné sera définitivement supprimé.')}
                </p>
              </div>
            )}

            {mergeSourceCounterpartyId && (mergeAccountId || mergeOperationType || mergeAvailableAccounts.length === 1) && (
              <div className="mb-4 p-3 bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg">
                <p className="text-sm text-blue-700 dark:text-blue-300">
                  ℹ️ {t('counterparties.merge.partialMergeNote', 'Note : le tiers source ne sera pas supprimé car un filtre est appliqué (fusion partielle).')}
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowMergeModal(false);
                  setMergeTargetCounterparty(null);
                  setMergeSourceCounterpartyId(undefined);
                  setMergeAccountId(undefined);
                  setMergeOperationType(undefined);
                }}
                className="flex-1 px-4 py-2 border border-slate-300 dark:border-slate-600 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
              >
                {t('common.cancel', 'Annuler')}
              </button>
              <button
                onClick={confirmMerge}
                disabled={isMerging || !mergeSourceCounterpartyId || !mergeOperationType}
                className="flex-1 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {isMerging ? t('common.loading', 'Chargement...') : t('counterparties.merge.confirm', 'Fusionner')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
